import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, ThumbsUp, Laugh, Zap, Heart, Hand, Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { playTone, soundEnabled, setSoundEnabled } from "@/lib/sounds";

export const REACTIONS = {
  fire: { icon: Flame, label: "Fire", color: "text-orange-400" },
  gg: { icon: ThumbsUp, label: "GG", color: "text-green-400" },
  laugh: { icon: Laugh, label: "Haha", color: "text-yellow-300" },
  wow: { icon: Zap, label: "Wow", color: "text-sky-400" },
  love: { icon: Heart, label: "Love", color: "text-pink-400" },
  wave: { icon: Hand, label: "Hi", color: "text-white" },
};

export function RoomReactions({ roomId, canReact }) {
  const [floating, setFloating] = useState([]);
  const [cooldown, setCooldown] = useState(false);
  const [sound, setSound] = useState(soundEnabled());

  useEffect(() => {
    const h = (e) => {
      const r = { ...e.detail, x: 10 + Math.random() * 70 };
      playTone(r.key);
      setFloating((f) => [...f.slice(-12), r]);
      setTimeout(() => setFloating((f) => f.filter((x) => x.id !== r.id)), 2600);
    };
    window.addEventListener("room:reaction", h);
    return () => window.removeEventListener("room:reaction", h);
  }, []);

  const send = (key) => {
    if (cooldown) return;
    setCooldown(true);
    setTimeout(() => setCooldown(false), 800);
    api.post(`/rooms/${roomId}/react`, { reaction: key }).catch(() => {});
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-40 h-72 pointer-events-none z-40 overflow-hidden" data-testid="reaction-stream">
        <AnimatePresence>
          {floating.map((r) => {
            const R = REACTIONS[r.key] || REACTIONS.fire;
            return (
              <motion.div key={r.id} initial={{ y: 260, opacity: 0, scale: 0.6 }} animate={{ y: 0, opacity: [0, 1, 1, 0], scale: 1 }}
                exit={{ opacity: 0 }} transition={{ duration: 2.4, ease: "easeOut" }}
                className="absolute flex items-center gap-1.5 rounded-full bg-black/70 border border-white/10 px-3 py-1.5" style={{ left: `${r.x}%` }}>
                <R.icon className={`h-5 w-5 ${R.color}`} />
                <span className="text-xs font-semibold text-white/80">{r.username}</span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      {canReact && (
        <div className="mt-6 flex justify-center gap-2 flex-wrap" data-testid="reaction-bar">
          {Object.entries(REACTIONS).map(([key, R]) => (
            <motion.button key={key} whileTap={{ scale: 0.85 }} onClick={() => send(key)} disabled={cooldown} data-testid={`reaction-${key}`}
              className="h-11 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-1.5 transition-colors disabled:opacity-50">
              <R.icon className={`h-4 w-4 ${R.color}`} /><span className="text-xs text-white/70">{R.label}</span>
            </motion.button>
          ))}
          <button onClick={() => { setSoundEnabled(!sound); setSound(!sound); }} data-testid="reaction-sound-toggle"
            aria-label={sound ? "Mute reactions" : "Unmute reactions"}
            className="h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
            {sound ? <Volume2 className="h-4 w-4 text-white/70" /> : <VolumeX className="h-4 w-4 text-white/40" />}
          </button>
        </div>
      )}
    </>
  );
}
